import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppState } from "../../app-state";
import { enrichWalletSatSources, fetchWalletSatSources } from "../../lib/api";
import { walletSatSourceIsUsable } from "../../lib/rarity";
import type { WalletSatSource } from "../../lib/types";
import type { RareSatPickerProps } from "./RareSatPicker";

export function toggleRareSatOutpointSelection(current: string[], outpoint: string, limit: number): string[] {
  if (current.includes(outpoint)) return current.filter((candidate) => candidate !== outpoint);
  if (current.length >= limit) return current;
  return [...current, outpoint];
}

export function useWalletRareSatSelection(selectionLimit: number) {
  const { wallet, setWalletModalOpen } = useAppState();
  const [sources, setSources] = useState<WalletSatSource[]>([]);
  const [selectedOutpoints, setSelectedOutpoints] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    requestRef.current += 1;
    setSources([]);
    setSelectedOutpoints([]);
    setSearch("");
    setLoading(false);
    setEnriching(false);
    setLoaded(false);
    setError("");
  }, [wallet?.ordinals.address]);

  useEffect(() => {
    setSelectedOutpoints((current) => current.length > selectionLimit ? current.slice(0, selectionLimit) : current);
  }, [selectionLimit]);

  const load = useCallback(async () => {
    if (!wallet) {
      setWalletModalOpen(true);
      return;
    }
    const address = wallet.ordinals.address;
    const requestId = ++requestRef.current;
    setLoading(true);
    setEnriching(false);
    setLoaded(false);
    setError("");
    try {
      const available = await fetchWalletSatSources(address);
      if (requestId !== requestRef.current) return;
      setSources(available);
      setSelectedOutpoints((current) => current.filter((outpoint) => available.some((source) => source.outpoint === outpoint)));
      setLoading(false);
      setLoaded(true);
      setEnriching(true);
      void enrichWalletSatSources(address, available)
        .then((enriched) => {
          if (requestId !== requestRef.current) return;
          setSources(enriched);
          setSelectedOutpoints((current) => current.filter((outpoint) => enriched.some((source) => source.outpoint === outpoint && walletSatSourceIsUsable(source))));
        })
        .catch(() => {
          if (requestId !== requestRef.current) return;
          setError("Special-sat classifications are unavailable. Unclassified sats remain unknown, not confirmed common.");
        })
        .finally(() => {
          if (requestId === requestRef.current) setEnriching(false);
        });
    } catch (caught) {
      if (requestId !== requestRef.current) return;
      setError(caught instanceof Error ? caught.message : "Could not load wallet sats");
      setLoaded(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [setWalletModalOpen, wallet]);

  const toggle = useCallback((source: WalletSatSource) => {
    setError("");
    if (!walletSatSourceIsUsable(source)) {
      setError("Only the first sat of a UTXO can be used");
      return;
    }
    setSelectedOutpoints((current) => toggleRareSatOutpointSelection(current, source.outpoint, selectionLimit));
  }, [selectionLimit]);

  const selectedSources = useMemo(() => selectedOutpoints
    .map((outpoint) => sources.find((source) => source.outpoint === outpoint))
    .filter((source): source is WalletSatSource => Boolean(source)), [selectedOutpoints, sources]);

  const clear = useCallback(() => {
    setSelectedOutpoints([]);
    setError("");
  }, []);

  return {
    selectedSources,
    clear,
    pickerProps: {
      walletConnected: Boolean(wallet),
      sources,
      selectedOutpoints,
      search,
      loading,
      enriching,
      loaded,
      error,
      selectionLimit,
      onLoad: () => { void load(); },
      onSearch: setSearch,
      onToggle: toggle,
      onClear: clear,
    } satisfies RareSatPickerProps,
  };
}
